import {
	BUYER_ADMIN,
	BUYER_ONLY_NO_STORE_ASSOCIATE,
	BUYER_STORE_ASSOCIATE,
	BUYER_USER,
	SUPER_ADMIN,
	SUPPLIER_ACCOUNTING_MANAGER,
	SUPPLIER_ADMIN,
	SUPPLIER_INVENTORY_MANAGER,
	SUPPLIER_MANAGER,
	SUPPLIER_TECH,
	SUPPLIER_USER,
} from '../components/roles/roles';
import { WORK_ORDER_DISPLAY_STATUSES } from '../constants/WorkOrderDisplayStatuses';
import {
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../constants/NoteTypes';
import { PENDING_APPROVAL_STATUSES, WORK_ORDER_STATUSES } from '../constants/WorkOrderStatuses';
import {
	getBuyerCompanySettingValue,
	isBuyerOrInternalTech,
	isInternalTech,
	isThirdPartySupplier,
} from './AuthUtils';
import {
	getCurrency,
	isContractor,
	isSupplierPayingCustomerAndAdmin,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZeroOrElse,
	recordHasContractorId,
	valueCompareWithoutType,
} from './DataAccessUtils';
import { CONTRACTOR_PROPS, FILTER_CONFIG_NAMES, ROLE_TYPES, WO_STEP_NAMES } from './DataConstants';
import { logError, logWarn, LOG_EVENTS } from './TelemetryLogUtils';
import moment from 'moment';
import { getContactName } from './DataFormatterUtils';
import { getLinkWIthBackLinkParams } from './HistoryUtils';
import WalkthroughTaskListAnswerDisplay from '../components/walkthroughs_detail_details_page/WalkthroughTaskListAnswerDisplay';
import { getAnswerItems } from './TaskItemUtils';
import { SITE_SURVEY_ANSWER_TYPE } from './SiteSurveyUtils';
import posthog from 'posthog-js';
import { mergeConvertedImages } from './ConvertedImageUtils';

export const UNREAD_NOTES_NOTIFICATION_KEY = 'isLastNoteUnreadByCurrentUser';

export const DEFAULT_NOTIFICATION_DURATION = 6.5;

export const hasUnreadNotesNotifications = (workOrder) =>
	nullSafeGetOrElse('unreadNotesNotificationIds.length', workOrder, 0) > 0 ||
	nullSafeGet(UNREAD_NOTES_NOTIFICATION_KEY, workOrder);

/**
 * Check if there are unread notes for a specific view (client vs contractor notes).
 * For subcontracted work orders, client notes and contractor notes have separate unread flags.
 * @param workOrder - The work order object
 * @param isClientView - true for client notes, false for contractor/subcontractor notes
 * @returns true if there are unread notes for the specified view
 */
export const hasUnreadNotesForView = (workOrder, isClientView: boolean) => {
	if (!workOrder) return false;

	// For subcontracted work orders, check the appropriate nested work order's unread flags
	if (isClientView) {
		// Client view: check main work order or subContractedFromWorkOrder
		const clientWO = nullSafeGet('subContractedFromWorkOrder', workOrder) || workOrder;
		return hasUnreadNotesNotifications(clientWO);
	} else {
		// Contractor/Subcontractor view: check subContractedToWorkOrder
		const contractorWO = nullSafeGet('subContractedToWorkOrder', workOrder);
		if (contractorWO) {
			return hasUnreadNotesNotifications(contractorWO);
		}
		// Fallback to main work order if no contractor work order exists
		return hasUnreadNotesNotifications(workOrder);
	}
};

/*
    1. Is internal supplier? => null
    2. Is asset warranty? => asset warranty nte
    3. Has existing NTE? => Existing NTE
    4. Has work order attributes NTE? => Work Order Attribute NTE
    5. Has no NTE? => undefined
 */
export const getNewLocationNTE = (
	hasSupplier,
	privateSupplier,
	assetWarrantyNTE,
	locationNTE,
	workOrderAttributesNTE,
	currencyExchangeRateBuyerToLocation
) =>
	!hasSupplier || privateSupplier
		? null //1. Is internal supplier? => null
		: assetWarrantyNTE
		? assetWarrantyNTE //2. Is asset warranty? => asset warranty nte
		: locationNTE
		? locationNTE // 3. Has existing NTE? => Existing NTE
		: workOrderAttributesNTE
		? workOrderAttributesNTE * currencyExchangeRateBuyerToLocation //4. Has work order attributes NTE? => Work Order Attribute NTE
		: 0; //5. Has no NTE? => 0

export const BUYER_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.AREA,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.ASSET,
	FILTER_CONFIG_NAMES.ASSET_TYPE,
	FILTER_CONFIG_NAMES.PROBLEM_TYPE,
	FILTER_CONFIG_NAMES.SUPPLIER,
	FILTER_CONFIG_NAMES.WO_LABELS,
	FILTER_CONFIG_NAMES.STARRED,
	FILTER_CONFIG_NAMES.BRAND,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.RESOLUTION_TYPE,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_PAST_DUE,
	FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE,
	FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
	FILTER_CONFIG_NAMES.IS_CAPEX,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
	FILTER_CONFIG_NAMES.HAS_REFRIGERANT_EVENTS,
	FILTER_CONFIG_NAMES.PM_SCHEDULE,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const INTERNAL_SERVICE_TEAM_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.AREA,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUPPLIER,
	FILTER_CONFIG_NAMES.WO_LABELS,
	FILTER_CONFIG_NAMES.STARRED,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.ASSET,
	FILTER_CONFIG_NAMES.ASSET_TYPE,
	FILTER_CONFIG_NAMES.PROBLEM_TYPE,
	FILTER_CONFIG_NAMES.BRAND,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.RESOLUTION_TYPE,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_PAST_DUE,
	FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE,
	FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
	FILTER_CONFIG_NAMES.IS_CAPEX,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
	FILTER_CONFIG_NAMES.HAS_REFRIGERANT_EVENTS,
	FILTER_CONFIG_NAMES.PM_SCHEDULE,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const THIRD_PARTY_SUPPLIER_WO_FILTERS_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
	FILTER_CONFIG_NAMES.WO_LABELS,
	FILTER_CONFIG_NAMES.STARRED,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.TRADE,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
	FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
];

export const PAYING_SUPPLIER_WO_FILTER_ORDER = [
	FILTER_CONFIG_NAMES.LOCATION,
	FILTER_CONFIG_NAMES.CONTRACTOR_CLIENT,
	FILTER_CONFIG_NAMES.TECHNICIAN,
	FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
	FILTER_CONFIG_NAMES.WO_LABELS,
	FILTER_CONFIG_NAMES.STARRED,
	FILTER_CONFIG_NAMES.PRIORITY,
	FILTER_CONFIG_NAMES.TRADE,
	FILTER_CONFIG_NAMES.CREATED_BY,
	FILTER_CONFIG_NAMES.NOTES_STATUS,
	FILTER_CONFIG_NAMES.CREATED_AT,
	FILTER_CONFIG_NAMES.COMPLETED_AT,
	FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
	FILTER_CONFIG_NAMES.IS_STALE,
	FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE,
	FILTER_CONFIG_NAMES.IS_WARRANTY,
];

export const getAssetFilterForWOCreation = (userType) => (companyConfig, currentUser, state) => {
	const areaAllowed = getBuyerCompanySettingValue(userType)(
		'assetConfig.allowAreaAssociationWithAssets',
		companyConfig,
		currentUser,
		false
	);

	return {
		locationId: nullSafeGet('location.id', state),
		problemTypeId: nullSafeGet('problemType.id', state),
		topLevelOnly: 'true',
		...(areaAllowed &&
			nullSafeGet('area.id', state) && {
				areaIdOrNull: nullSafeGet('area.id', state),
			}),
	};
};

export const getSupplierWorkOrderServiceCalls = (workOrder, currentUser, isClientView) => {
	const sanitisedWO = getSanitisedWorkOrder(workOrder, isClientView);
	const allServiceCalls = nullSafeGetOrElse('associatedServiceCalls', sanitisedWO, []);
	// if internalTech show all service calls
	if (isInternalTech(currentUser)) {
		return allServiceCalls;
	}
	return allServiceCalls.filter(
		(_) =>
			_.supplierFacilityId === sanitisedWO.supplierFacilityId ||
			_.supplierFacilityId === sanitisedWO.subContractorSupplierFacilityId
	);
};

// Mirrors ActiveWorkCancellationLockConfig.DefaultCheckInConsideredStaleAfterHours on the backend,
// which applies the same fallback when the stored value is absent or nonpositive. Keep in sync.
export const DEFAULT_CHECK_IN_CONSIDERED_STALE_AFTER_HOURS = 24;

// The lock is a buyer company setting and the backend resolves it from the work order's own
// buyerCompanyId. The buyer app has those settings in the company_config slice; the supplier app
// only ever sees them hydrated onto the work order, which is the same source the sibling helper
// getServiceCallConfigValueBasedOnSupplierOrInternalTeam reads from.
const getActiveWorkCancellationLockConfig = (workOrder, userType, companyConfig) =>
	userType === ROLE_TYPES.BUYER
		? nullSafeGetOrElse('config.workOrder.activeWorkCancellationLock', companyConfig, {})
		: nullSafeGetOrElse(
				'buyerCompany.buyerCompanySettings.config.workOrder.activeWorkCancellationLock',
				workOrder,
				{}
		  );

export const getCheckedInTechnicianEmail = (serviceCall) =>
	nullSafeGet('checkInByEmail', serviceCall) || nullSafeGet('leadTechnicianEmail', serviceCall);

// Mirrors ActiveCheckInUtil.isActivelyCheckedIn: a visit counts as live work only while its
// check-in is younger than the configured window. An open check-in older than that is treated as a
// forgotten check-out and stops blocking on its own, so it must not disable the button forever.
const isActivelyCheckedIn = (serviceCall, checkInConsideredStaleAfterHours) => {
	const checkInTime = nullSafeGet('checkInTime', serviceCall);
	return (
		!nullSafeGet('checkOutTime', serviceCall) &&
		!!checkInTime &&
		moment(checkInTime).isAfter(moment().subtract(checkInConsideredStaleAfterHours, 'hours'))
	);
};

/**
 * Client-side mirror of WorkOrderServiceImpl.rejectCancellationWhileTechnicianCheckedIn, used only
 * to disable the cancel affordance up front. The backend re-checks on every write and is still the
 * authority — it owns the clock and can see visits this payload may not carry — so a caller must
 * keep handling the 409 rather than treating a null here as permission to cancel.
 *
 * Returns the service call blocking cancellation, or null when nothing blocks.
 */
export const getCancellationBlockingServiceCall = (workOrder, userType, companyConfig) => {
	const lockConfig = getActiveWorkCancellationLockConfig(workOrder, userType, companyConfig);
	if (!nullSafeGetOrElse('enabled', lockConfig, false)) {
		return null;
	}

	// A nonpositive window would put the cutoff in the future and let every check-in through, so the
	// backend discards it in favour of the default. Mirror that rather than unlocking the button.
	const configuredHours = nullSafeGet('checkInConsideredStaleAfterHours', lockConfig);
	const checkInConsideredStaleAfterHours =
		configuredHours > 0 ? configuredHours : DEFAULT_CHECK_IN_CONSIDERED_STALE_AFTER_HOURS;

	// Cancelling a work order cancels every sub-contract underneath it, so a contractor visit blocks
	// it too. Same union the backend scans.
	const activeServiceCalls = [
		...nullSafeGetOrElse('associatedServiceCalls', workOrder, []),
		...nullSafeGetOrElse('associatedContractorServiceCalls', workOrder, []),
	].filter((serviceCall) => isActivelyCheckedIn(serviceCall, checkInConsideredStaleAfterHours));

	// Prefer a visit that names its technician, so an unnamed visit can't hide a named one.
	return (
		activeServiceCalls.find((serviceCall) => getCheckedInTechnicianEmail(serviceCall)) ||
		activeServiceCalls[0] ||
		null
	);
};

/**
 * Kept identical to the copy TechnicianCheckedInException raises, so the reason reads the same
 * whether it was caught up front or came back as a 409.
 */
export const getCancellationBlockedReason = (blockingServiceCall) => {
	const technicianEmail = getCheckedInTechnicianEmail(blockingServiceCall);
	const technicianDescription = technicianEmail ? `technician ${technicianEmail}` : 'a technician';
	return `You can’t cancel this work order: ${technicianDescription} is currently checked in.`;
};

export const isAllowedToEditTasksByWOStatus = (workOrder, currentUser) => {
	const allowedByWOStatus =
		workOrder.displayStatus !== WORK_ORDER_DISPLAY_STATUSES.complete &&
		workOrder.displayStatus !== WORK_ORDER_DISPLAY_STATUSES.cancelled &&
		workOrder.status !== WORK_ORDER_STATUSES.waitingForReview;

	const isPrivateSupplier =
		nullSafeGet('facility.status', currentUser) === 'private' && allowedByWOStatus;
	const allowedForPayingCustomer =
		nullSafeGet('facility.id', currentUser) !== workOrder.supplierFacilityId &&
		nullSafeGet('isSupplierInitiated', workOrder) &&
		isSupplierPayingCustomerAndAdmin(currentUser) &&
		allowedByWOStatus;

	const allowedForThirdPartyBasedOnStatus =
		!isPrivateSupplier && workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.pending;

	if (currentUser.userType === ROLE_TYPES.SUPPLIER) {
		return isPrivateSupplier || allowedForPayingCustomer || allowedForThirdPartyBasedOnStatus;
	} else {
		return allowedByWOStatus;
	}
};

export const isWOTaskCheckFailed = (workOrder, tasks = []) =>
	nullSafeGetOrElse('taskCompletionMandatory', workOrder, false) &&
	!!tasks.find((_) => nullSafeGetOrElse('answer', _, 'false') !== 'true');

export const getFieldsSetForWorkOrderDefaults = (location, submittedByNameIsRequired) => {
	const urlParams = new URLSearchParams(location.search);
	const locationId = urlParams.get('locationId');
	const areasTopLevel = urlParams.get('areasTopLevel');
	const areaId = urlParams.get('areaId');
	const areaIdNextLevel = urlParams.get('areaIdNextLevel');
	const assetId = urlParams.get('assetId');
	const problemTypeId = urlParams.get('problemTypeId');
	const problemTypeTopLevel = urlParams.get('problemTypeTopLevel');
	const problemTypeIdNextLevel = urlParams.get('problemTypeIdNextLevel');
	const isAssetDown = urlParams.get('isAssetDown');
	const supplierFacilityId = urlParams.get('supplierFacilityId');
	const submittedByName = urlParams.get('submittedByName');
	const defaultPriorityId = urlParams.get('defaultPriorityId');
	const walkthroughId = urlParams.get('walkthroughId');
	const siteSurveyTaskTitleId = urlParams.get('siteSurveyTaskTitleId');

	const fieldsToSet: any = {};
	if (locationId) fieldsToSet.locationId = locationId;
	if (areaId) fieldsToSet.areaId = areaId;
	if (assetId) fieldsToSet.assetId = assetId;
	if (areasTopLevel) fieldsToSet.areasTopLevel = areasTopLevel;
	if (areaIdNextLevel) fieldsToSet.areaIdNextLevel = areaIdNextLevel;
	if (problemTypeTopLevel) fieldsToSet.problemTypeTopLevel = problemTypeTopLevel;
	if (problemTypeId) fieldsToSet.problemTypeId = problemTypeId;
	if (problemTypeIdNextLevel) fieldsToSet.problemTypeIdNextLevel = problemTypeIdNextLevel;
	if (isAssetDown) fieldsToSet.isAssetDown = isAssetDown;
	if (supplierFacilityId) fieldsToSet.supplierFacilityId = supplierFacilityId;
	if (submittedByName && submittedByNameIsRequired) fieldsToSet.submittedByName = submittedByName;
	if (defaultPriorityId) fieldsToSet.woPriorityId = defaultPriorityId;
	if (walkthroughId) fieldsToSet.walkthroughId = walkthroughId;
	if (siteSurveyTaskTitleId) fieldsToSet.siteSurveyTaskTitleId = siteSurveyTaskTitleId;
	return fieldsToSet;
};

export const getLinkedWOStateDefaults = (fieldsToSet) => {
	const stateDefaults: any = {};

	if (fieldsToSet.areasTopLevel) {
		stateDefaults.areaLevel1Id = fieldsToSet.areasTopLevel;
	}
	if (fieldsToSet.areaId) {
		stateDefaults.areaLevel2Id = fieldsToSet.areaId;
		stateDefaults.area2Available = true;
	}
	if (fieldsToSet.areaIdNextLevel) {
		stateDefaults.areaLevel3Id = fieldsToSet.areaIdNextLevel;
		stateDefaults.area3Available = true;
	}
	if (fieldsToSet.problemTypeTopLevel) {
		stateDefaults.problemTypeLevel1Id = fieldsToSet.problemTypeTopLevel;
	}
	if (fieldsToSet.problemTypeId) {
		stateDefaults.problemTypeLevel2Id = fieldsToSet.problemTypeId;
		stateDefaults.problemTypeLevel2Available = true;
	}
	if (fieldsToSet.problemTypeIdNextLevel) {
		stateDefaults.problemTypeLevel3Id = fieldsToSet.problemTypeIdNextLevel;
		stateDefaults.problemTypeLevel3Available = true;
	}

	return stateDefaults;
};

export const getApprovedOrDeclinedDetail = (workOrder) => {
	const approvedOrDeniedByContact = nullSafeGet('approvedOrDeniedByContact', workOrder);
	const actionName =
		nullSafeGet('displayStatus', workOrder) !== WORK_ORDER_DISPLAY_STATUSES.cancelled &&
		!PENDING_APPROVAL_STATUSES.includes(nullSafeGet('status', workOrder))
			? 'Approved'
			: null;

	return approvedOrDeniedByContact && actionName
		? {
				keyName: `${actionName} by`,
				contactObject: approvedOrDeniedByContact,
		  }
		: null;
};

export const isDifferentAssignedSupplier = (workOrder, currentUser) => {
	const facilityId = nullSafeGet('facility.id', currentUser);
	return (
		facilityId &&
		facilityId !== workOrder.supplierFacilityId &&
		facilityId !== nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder)
	);
};

export const isDifferentAssignedSupplierBasedOnView = (workOrder, currentUser, isClientView) => {
	const facilityId = nullSafeGet('facility.id', currentUser);
	return (
		facilityId &&
		(isClientView
			? facilityId !== workOrder.supplierFacilityId &&
			  facilityId !== nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder)
			: true) //If it is subcontractor view, it is definitely assigned for a different supplier
	);
};

export const isManagedBuyerCompanyIdWO = (workOrder, currentUser) => {
	const supplierCompanyId = nullSafeGet('facility.supplierCompanyId', currentUser);
	return (
		supplierCompanyId &&
		supplierCompanyId === nullSafeGet('buyerCompany.managedBySupplierCompanyId', workOrder)
	);
};

export const biddingOnWorkOrderAssociatedWithAnotherSupplier = (workOrder, currentUser) => {
	const isThirdPartySupplier =
		currentUser.userType === ROLE_TYPES.SUPPLIER && !isInternalTech(currentUser);

	return isThirdPartySupplier && isDifferentAssignedSupplier(workOrder, currentUser);
};

export const isSubContractedWorkOrder = (workOrder) =>
	!!nullSafeGet('subContractedToWorkOrder.subSupplierFacilityId', workOrder);

export const isSubContractedFromWorkOrder = (workOrder) =>
	!!nullSafeGet('subContractedFromWorkOrder.subSupplierFacilityId', workOrder);

const getSubcontractedValues = (
	entity,
	woPropName,
	contractorWOPropName,
	isClientView,
	lookForToWOAsWell = false
) => {
	// Project invoices have no work order at all, so this runs with entity === undefined.
	const getValue = (woEntityName) =>
		nullSafeGetWithZeroOrElse(
			`${woEntityName}.${contractorWOPropName}`,
			entity,
			entity ? entity[woPropName] : undefined
		);

	const clientValue = getValue('subContractedFromWorkOrder');
	const toWOValue = getValue('subContractedToWorkOrder');

	return isClientView
		? clientValue
			? clientValue
			: lookForToWOAsWell
			? toWOValue
			: clientValue
		: toWOValue;
};

const getWOPropValuesFromMayBeClient = (
	workOrder,
	contractorPropName,
	woPropName,
	isClientView
) => {
	const clientValue = nullSafeGet(`subContractedFromWorkOrder.${contractorPropName}`, workOrder);
	const toWOValue = nullSafeGet(`subContractedToWorkOrder.${contractorPropName}`, workOrder);
	const woValue = workOrder ? workOrder[woPropName] : undefined;

	return nullSafeGet('locationId', workOrder) === -100
		? isClientView
			? clientValue || toWOValue
			: toWOValue
		: isClientView
		? clientValue || woValue || toWOValue
		: toWOValue || woValue;
};

export const getSanitisedWorkOrder = (workOrder, isClientView = true) => ({
	...workOrder,
	originalWorkOrder: isSubContractedWorkOrder(workOrder) ? workOrder : undefined,
	title: getSubcontractedValues(workOrder, 'title', 'updatedTitle', isClientView),
	description: getSubcontractedValues(workOrder, 'description', 'updatedDescription', isClientView),
	nte: getSubcontractedValues(workOrder, 'nte', 'updatedNte', isClientView),
	status: getSubcontractedValues(workOrder, 'status', 'contractorStatus', isClientView),
	estimatedCompletionDate: getSubcontractedValues(
		workOrder,
		'estimatedCompletionDate',
		'subEstimatedCompletionDate',
		isClientView
	),
	displayStatus: getSubcontractedValues(
		workOrder,
		'displayStatus',
		'contractorDisplayStatus',
		isClientView
	),
	supplierAttachments: isClientView
		? nullSafeGet('subContractedFromWorkOrder', workOrder)
			? nullSafeGetOrElse('subContractedFromWorkOrder.subContractorAttachments', workOrder, [])
			: nullSafeGetOrElse('supplierAttachments', workOrder, [])
		: nullSafeGetOrElse('subContractedToWorkOrder.contractorAttachments', workOrder, []),
	subContractorAttachments: isClientView
		? []
		: nullSafeGetOrElse('subContractedToWorkOrder.subContractorAttachments', workOrder, []),

	contractorAttachments: isClientView
		? nullSafeGetOrElse('subContractedFromWorkOrder.contractorAttachments', workOrder, [])
		: [],
	// The attachment fields above are drawn from the work order itself AND from either side of a
	// sub-contract, so the conversions have to cover all three sources or a sub-contracted view
	// would fall back to the undecodable original. Safe to merge: refs match on a globally unique
	// fileId, so one whose attachment is not on screen simply never matches.
	convertedImages: mergeConvertedImages(
		nullSafeGet('convertedImages', workOrder),
		nullSafeGet('subContractedFromWorkOrder.convertedImages', workOrder),
		nullSafeGet('subContractedToWorkOrder.convertedImages', workOrder)
	),
	location: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorLocation',
		'location',
		isClientView
	),
	problemType: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorProblemType',
		'problemType',
		isClientView
	),
	woPriority: getWOPropValuesFromMayBeClient(
		workOrder,
		'contractorWorkOrderPriority',
		'woPriority',
		isClientView
	),
	contractorClient: getSubcontractedValues(
		workOrder,
		'contractorClient',
		'contractorClient',
		isClientView,
		true
	),
	associatedServiceCalls: getSubcontractedValues(
		workOrder,
		'associatedServiceCalls',
		'associatedContractorServiceCalls',
		isClientView
	),
	lastServiceCall: getSubcontractedValues(
		workOrder,
		'lastServiceCall',
		'lastContractorServiceCall',
		isClientView
	),
	supplierFacilityId: getSubcontractedValues(
		workOrder,
		'supplierFacilityId',
		'subSupplierFacilityId',
		isClientView
	),
	supplierFacility: getSubcontractedValues(
		workOrder,
		'supplierFacility',
		'subSupplierFacility',
		isClientView
	),
	contractorWo: getContractorWO(workOrder, isClientView),
	buyerSubscribers: getSubcontractedValues(
		workOrder,
		'buyerSubscribers',
		'contractorSubscribers',
		isClientView
	),
	supplierSubscribers: getSubcontractedValues(
		workOrder,
		'supplierSubscribers',
		'subSubscribers',
		isClientView
	),
	hasSupplierArchived: getSubcontractedValues(
		workOrder,
		'hasSupplierArchived',
		'hasSubArchived',
		isClientView
	),
});

export const getWorkOrderfromContractedWorkOrder = (workOrder, isClientView = true) => ({
	...workOrder,
	title: nullSafeGetOrElse('updatedTitle', workOrder, nullSafeGet('title', workOrder)),
	displayStatus: nullSafeGetOrElse(
		'contractorDisplayStatus',
		workOrder,
		nullSafeGet('displayStatus', workOrder)
	),
	description: nullSafeGetOrElse(
		'updatedDescription',
		workOrder,
		nullSafeGet('description', workOrder)
	),
	nte: nullSafeGetOrElse('updatedNte', workOrder, nullSafeGet('nte', workOrder)),
	status: nullSafeGetOrElse('contractorStatus', workOrder, nullSafeGet('status', workOrder)),
	supplierFacilityId: nullSafeGetOrElse(
		'subSupplierFacilityId',
		workOrder,
		nullSafeGet('supplierFacilityId', workOrder)
	),
	supplierFacility: nullSafeGetOrElse(
		'subSupplierFacility',
		workOrder,
		nullSafeGet('supplierFacility', workOrder)
	),
});

export const getContractorWO = (workOrder, isClientView = true) =>
	isClientView
		? nullSafeGet('subContractedFromWorkOrder', workOrder)
		: nullSafeGet('subContractedToWorkOrder', workOrder);

export const applyMarkup = (quote, markupPercentage) => {
	const markupFactor = 1 + markupPercentage / 100;

	function applyMarkupToLineItems(lineItems) {
		return lineItems.map((item) => ({
			...item,
			hourlyRate: (parseFloat(item.hourlyRate) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	function applyMarkupToMaterials(materialItems) {
		return materialItems.map((item) => ({
			...item,
			unitPrice: (parseFloat(item.unitPrice) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToMisc(miscItems) {
		return miscItems.map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	return {
		...quote,
		laborTotalBeforeTax: (parseFloat(quote.laborTotalBeforeTax) * markupFactor).toFixed(2),
		laborLineItems: applyMarkupToLineItems(quote.laborLineItems),
		materialTotalBeforeTax: (parseFloat(quote.materialTotalBeforeTax) * markupFactor).toFixed(2),
		materialLineItems: applyMarkupToMaterials(quote.materialLineItems),
		totalBeforeTax: (parseFloat(quote.totalBeforeTax) * markupFactor).toFixed(2),
		totalAfterTax: (parseFloat(quote.totalAfterTax) * markupFactor).toFixed(2),
		tax: (parseFloat(quote.tax) * markupFactor).toFixed(2),
		incurredCostTotalBeforeTax: (parseFloat(quote.tax) * markupFactor).toFixed(2),
		travelTotalBeforeTax: (parseFloat(quote.travelTotalBeforeTax) * markupFactor).toFixed(2),
		freightTotalBeforeTax: (parseFloat(quote.freightTotalBeforeTax) * markupFactor).toFixed(2),
		miscTotalBeforeTax: (parseFloat(quote.miscTotalBeforeTax) * markupFactor).toFixed(2),
		miscLineItems: applyMarkupToMisc(quote.miscLineItems),
	};
};
export const applyMarkupToInvoice = (invoice, markupPercentage) => {
	const markupFactor = 1 + markupPercentage / 100;

	function applyMarkupToLineItems(lineItems) {
		return lineItems.map((item) => ({
			...item,
			hourlyRate: (parseFloat(item.hourlyRate) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	function applyMarkupToMaterials(materialItems) {
		return materialItems.map((item) => ({
			...item,
			unitPrice: (parseFloat(item.unitPrice) * markupFactor).toFixed(2),
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToMisc(miscItems) {
		return miscItems.map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}
	function applyMarkupToTaxItems(miscItems = []) {
		return (miscItems || []).map((item) => ({
			...item,
			amount: (parseFloat(item.amount) * markupFactor).toFixed(2),
		}));
	}

	return {
		...invoice,
		laborTotalBeforeTax: (parseFloat(invoice.laborTotalBeforeTax) * markupFactor).toFixed(2),
		laborLineItems: applyMarkupToLineItems(invoice.laborLineItems),
		materialTotalBeforeTax: (parseFloat(invoice.materialTotalBeforeTax) * markupFactor).toFixed(2),
		materialLineItems: applyMarkupToMaterials(invoice.materialLineItems),
		invoiceTotalBeforeTax: (parseFloat(invoice.invoiceTotalBeforeTax) * markupFactor).toFixed(2),
		invoiceTotalAfterTax: (parseFloat(invoice.invoiceTotalAfterTax) * markupFactor).toFixed(2),
		invoiceTax: (parseFloat(invoice.invoiceTax) * markupFactor).toFixed(2),
		incurredCostTotalBeforeTax: (parseFloat(invoice.tax) * markupFactor).toFixed(2),
		travelTotalBeforeTax: (parseFloat(invoice.travelTotalBeforeTax) * markupFactor).toFixed(2),
		freightTotalBeforeTax: (parseFloat(invoice.freightTotalBeforeTax) * markupFactor).toFixed(2),
		miscTotalBeforeTax: (parseFloat(invoice.miscTotalBeforeTax) * markupFactor).toFixed(2),
		miscLineItems: applyMarkupToMisc(invoice.miscLineItems),
		taxLineItems: applyMarkupToTaxItems(invoice.taxLineItems),
	};
};

export const getSanitisedQuote = (quote, isClientView = true) => {
	return {
		...quote,
		supplierFacilityId: nullSafeGetOrElse(
			'supplierFacilityId',
			quote,
			nullSafeGet('subSupplierFacilityId', quote)
		),
		supplierFacility: nullSafeGetOrElse(
			'supplierFacility',
			quote,
			nullSafeGet('subSupplierFacility', quote)
		),
		workOrder: getSanitisedWorkOrder(quote.workOrder, isClientView),
	};
};
export const getSanitisedInvoice = (invoice, isClientView = true) => ({
	...invoice,
	supplierFacilityId: nullSafeGetOrElse(
		'supplierFacilityId',
		invoice,
		nullSafeGet('subSupplierFacilityId', invoice)
	),
	supplierFacility: nullSafeGetOrElse(
		'supplierFacility',
		invoice,
		nullSafeGet('subSupplierFacility', invoice)
	),
	workOrder: getWorkOrderfromContractedWorkOrder(invoice.workOrder || invoice.contractorWorkOrder),
});

export const getContractorOrWorkOrderId = (workOrder) => {
	return nullSafeGet('contractorWo.id', workOrder) || nullSafeGet('id', workOrder);
};

export const getInternalNotesChannelId = (woId) => {
	return 'internal_' + woId;
};

export const getContractorOrWorkOrderIdBasedOnView = (workOrder, isClientView) => {
	return isClientView
		? nullSafeGetOrElse(
				`${CONTRACTOR_PROPS.FROM_WO}.id`,
				workOrder,
				nullSafeGetOrElse('contractorWo.id', workOrder, nullSafeGet('id', workOrder))
		  )
		: nullSafeGetOrElse(
				`${CONTRACTOR_PROPS.TO_WO}.id`,
				workOrder,
				nullSafeGetOrElse('contractorWo.id', workOrder, nullSafeGet('id', workOrder))
		  );
};

export const getBuyerOrContractorOrClientDetailsFromSearch = (workOrder) => {
	const subContractedDetails = nullSafeGetOrElse('subContractedDetails', workOrder, []);
	const supplierFacilityId = workOrder.currentUserSupplierFacilityId;

	const subContractedFromDetailsFromSearch = subContractedDetails.find(
		(_) => _.subContractedToSupplierFacilityId === supplierFacilityId
	);
	const nameFromFromWOSearch = nullSafeGet(
		'subContractedFromSupplierFacilityName',
		subContractedFromDetailsFromSearch
	);
	const fromWOObj = nameFromFromWOSearch
		? {
				key: CONTRACTOR_PROPS.FROM_WO,
				value: {
					contractorSupplierFacility: {
						displayName: nameFromFromWOSearch,
					},
				},
		  }
		: null;

	const toWODetailFromSearch = subContractedDetails.find(
		(_) => _.subContractedFromSupplierFacilityId === supplierFacilityId
	);
	const contractorClientNameFromSearch = nullSafeGet('contractorClientName', toWODetailFromSearch);
	const toWOObj = contractorClientNameFromSearch
		? {
				key: CONTRACTOR_PROPS.TO_WO,
				value: {
					contractorClient: {
						name: contractorClientNameFromSearch,
					},
				},
		  }
		: null;

	const buyerCompanyName = nullSafeGet('buyerCompanyName', workOrder);
	const buyerCompanyObj = buyerCompanyName
		? {
				key: 'buyerCompany',
				value: {
					name: buyerCompanyName,
				},
		  }
		: null;

	return fromWOObj || buyerCompanyObj || toWOObj;
};

export const getBuyerOrContractorNameWithHighlightKey = (workOrder, currentUser) => {
	const thirdPartySupplier = isThirdPartySupplier(currentUser);
	const fromWOContractorSupplierFacilityName = nullSafeGet(
		`${CONTRACTOR_PROPS.FROM_WO}.contractorSupplierFacility.displayName`,
		workOrder
	);

	const toWOClientName = nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.contractorClient.name`, workOrder);

	const buyerCompanyName = nullSafeGet('buyerCompany.name', workOrder);

	const supplierFacilityName = nullSafeGet('supplierFacility.displayName', workOrder);

	if (thirdPartySupplier) {
		if (fromWOContractorSupplierFacilityName) {
			return {
				key: 'subContractedDetails.subContractedFromSupplierFacilityName',
				value: fromWOContractorSupplierFacilityName,
			};
		}
		if (buyerCompanyName) {
			return {
				key: 'buyerCompanyName',
				value: buyerCompanyName,
			};
		}
		if (toWOClientName) {
			return {
				key: 'subContractedDetails.contractorClientName',
				value: toWOClientName,
			};
		}
		return undefined;
	}

	if (supplierFacilityName) {
		return {
			key: 'supplierFacilityDisplayName',
			value: supplierFacilityName,
		};
	}

	return undefined;
};

export const isSelfClientWorkOrder = (workOrder, currentUser) => {
	const supplierFacilityId = nullSafeGet('facility.id', currentUser);
	const clientSupplierFacilityId = nullSafeGet(
		`${CONTRACTOR_PROPS.TO_WO}.contractorClient.clientSupplierFacilityId`,
		workOrder
	);
	return (
		supplierFacilityId &&
		clientSupplierFacilityId &&
		clientSupplierFacilityId === supplierFacilityId
	);
};

export const getWorkOrderPath = (userType, currentUser, record) => {
	const hasContractorId = recordHasContractorId(record);
	const isClientView = !isContractor(currentUser, record);
	return userType === ROLE_TYPES.SUPPLIER
		? hasContractorId
			? isClientView
				? ''
				: 'subcontractor_'
			: ''
		: '';
};

export const getWorkOrderArchivalKey = (currentUser: any) => {
	return isBuyerOrInternalTech(currentUser) ? 'hasBuyerArchived' : 'hasSupplierArchived';
};

export const getDueDate = (woPriority, currentDueDate) => {
	const expectedResolutionInSeconds = nullSafeGet('expectedResolutionInSeconds', woPriority);
	if (expectedResolutionInSeconds === null || expectedResolutionInSeconds === undefined) {
		if (currentDueDate) {
			return currentDueDate;
		} else {
			return moment();
		}
	} else {
		return moment(new Date(new Date().getTime() + expectedResolutionInSeconds * 1000));
	}
};

export const onCancelRequest = (props, state) => () => {
	const { history } = props;
	const { backLinkTo } = state;
	history.push(backLinkTo);
};

export const isAValidAndParitalWorkOrder = (workOrder: any): boolean => {
	if (!workOrder || !workOrder.id) {
		return false;
	}
	return workOrder.searchRecord === true;
};

export const getArchivedStatus = (defaultFilters, currentUser) => {
	return defaultFilters === null || defaultFilters === undefined
		? 'Active'
		: defaultFilters[getWorkOrderArchivalKey(currentUser)] === true
		? 'Archived'
		: defaultFilters[getWorkOrderArchivalKey(currentUser)] === undefined
		? 'All'
		: 'Active';
};

export const getNewMessageNotificationFromWSPayload = (data) => {
	const userAvailable = !!nullSafeGet('sentBy', data);
	const messageAvailable = !!data.message;

	if (userAvailable && messageAvailable) {
		return `${getContactName(data.sentBy)} says "${data.message}"`;
	} else if (userAvailable) {
		return `${getContactName(data.sentBy)} sent you a note!`;
	} else {
		return 'You have a new note!';
	}
};

export const isNoteValid = (note: any): boolean => {
	if (!note) {
		return false;
	}
	if (
		note.type === NOTE_TYPE_SERVICE_CALL_CHECK_IN ||
		note.type === NOTE_TYPE_SERVICE_CALL_CHECK_OUT ||
		note.type === NOTE_TYPE_INTERNAL_ATTACHMENT_LOG
	) {
		return true;
	}
	const hasText = note.text && note.text.length > 0;
	const hasPhoto = note.photo && note.photo.length > 0;
	const hasVideo = note.video && note.video.length > 0;
	const hasAudio = note.audio && note.audio.length > 0;
	const hasChangedBy = note.changedBy;

	return hasText || hasPhoto || hasVideo || hasAudio || hasChangedBy;
};

export const getWorkOrderLink = ({
	userType,
	tab,
	workOrder,
	targetCollectionName,
	location,
	BACK_TEXT,
}) => {
	const path = `/${userType}/workOrders/detail/${nullSafeGet('id', workOrder)}/${tab}${
		targetCollectionName ? `?targetCollectionName=${targetCollectionName}` : ''
	}`;
	return getLinkWIthBackLinkParams(location, BACK_TEXT, path, {
		preserve: true,
	});
};

export const getQuoteFilterConfig = ({ filters, userType, currentUser }) => {
	const isBuyer = userType !== 'supplier' || isInternalTech(currentUser);
	return [
		filters[FILTER_CONFIG_NAMES.LOCATION],
		filters[FILTER_CONFIG_NAMES.AREA],
		filters[FILTER_CONFIG_NAMES.PROBLEM_TYPE],
		...(isBuyer ? [filters[FILTER_CONFIG_NAMES.SUPPLIER]] : []),
		...(!isBuyer ? [filters[FILTER_CONFIG_NAMES.TRADE]] : []),
		...(isBuyer ? [filters[FILTER_CONFIG_NAMES.IS_CAPEX]] : []),
		filters[FILTER_CONFIG_NAMES.CREATED_AT],
		filters[FILTER_CONFIG_NAMES.COMPLETED_AT],
		filters[FILTER_CONFIG_NAMES.APPROVED_AT],
	].filter(Boolean);
};

export const getWorkOrderFilterConfig = ({ filters, currentUser = null }) => {
	return [
		filters[FILTER_CONFIG_NAMES.LOCATION],
		filters[FILTER_CONFIG_NAMES.AREA],
		filters[FILTER_CONFIG_NAMES.PRIORITY],
		...(isBuyerOrInternalTech(currentUser) ? [filters[FILTER_CONFIG_NAMES.ASSET_TYPE]] : []),
		filters[FILTER_CONFIG_NAMES.ASSET],
		filters[FILTER_CONFIG_NAMES.PROBLEM_TYPE],
		filters[FILTER_CONFIG_NAMES.SUPPLIER],
		filters[FILTER_CONFIG_NAMES.WO_LABELS],
		filters[FILTER_CONFIG_NAMES.STARRED],
		filters[FILTER_CONFIG_NAMES.BRAND],
		filters[FILTER_CONFIG_NAMES.RESOLUTION_TYPE],

		filters[FILTER_CONFIG_NAMES.IS_CAPEX],

		filters[FILTER_CONFIG_NAMES.CREATED_AT],
		filters[FILTER_CONFIG_NAMES.COMPLETED_AT],
		filters[FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT],
		filters[FILTER_CONFIG_NAMES.IS_PAST_DUE],
		filters[FILTER_CONFIG_NAMES.IS_PAST_SCHEDULE],
		filters[FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION],
		filters[FILTER_CONFIG_NAMES.NOTES_STATUS],
		filters[FILTER_CONFIG_NAMES.IS_STALE],
		filters[FILTER_CONFIG_NAMES.IS_PLANNED_MAINTENANCE],
		filters[FILTER_CONFIG_NAMES.IS_WARRANTY],
		filters[FILTER_CONFIG_NAMES.IS_ASSET_DOWN],
	].filter(Boolean);
};

export const getAllWOIdsToListenForWorkOrder = (workOrder): number[] => {
	const fromId = nullSafeGetOrElse(
		`${CONTRACTOR_PROPS.FROM_WO}.id`,
		workOrder,
		nullSafeGet('id', workOrder)
	);
	const toId = nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.id`, workOrder);
	const contractorWOIdsFromSearch = nullSafeGetOrElse('subContractedDetails', workOrder, [])
		.map((_) => _.id)
		.filter((_) => !!_);
	return [
		...new Set([
			...(fromId ? [fromId] : []),
			...(toId ? [toId] : []),
			...contractorWOIdsFromSearch,
		]),
	];
};

// The work order's OWN note-thread ids (client + contractor side), EXCLUDING the inaccessible
// root room and any sibling sub-contractor rooms the current supplier is NOT a party to. Those
// siblings belong to other facilities and their note broadcasts carry no facility id to filter
// on, so a listener joined to them would receive — and could surface — another facility's note.
// The client leg is the sub-contracted-from WO when this WO was sub-contracted TO this supplier,
// otherwise the WO's own id: when a FROM WO exists we must NOT join the root WO id, because the
// supplier has no access to the root thread and joining it would deliver root note payloads to
// the browser even though the view-scoped guard blocks the toast.
//
// When mounting from a supplier list/search record the sub-contracted WO id is not yet on
// subContractedToWorkOrder.id — it only lives in subContractedDetails — so we also include the
// ids of the entries the CURRENT supplier is a party to (as the sub-contracted-from or -to
// facility). That keeps the initial socket JOIN covering this supplier's own contractor/client
// thread without joining sibling entries it is not involved in. Used to scope the detail-page
// listeners so a supplier only ever subscribes to its own threads. (OPE-5075)
export const getOwnThreadWOIdsToListenForWorkOrder = (workOrder): number[] => {
	const clientLegId = nullSafeGetOrElse(
		`${CONTRACTOR_PROPS.FROM_WO}.id`,
		workOrder,
		nullSafeGet('id', workOrder)
	);
	const supplierFacilityId = nullSafeGet('currentUserSupplierFacilityId', workOrder);
	const ownSubContractedDetailIds = supplierFacilityId
		? nullSafeGetOrElse('subContractedDetails', workOrder, [])
				.filter(
					// Loose == on purpose: on list/search-backed work orders the facility ids can
					// arrive as strings while currentUserSupplierFacilityId is a number (or vice
					// versa). useNoteSending.ts matches the same field with == for this reason; a
					// strict compare here would drop the supplier's own contractor WO id from the
					// JOIN and miss live contractor/internal-note broadcasts until a remount.
					(detail) =>
						detail.subContractedFromSupplierFacilityId == supplierFacilityId ||
						detail.subContractedToSupplierFacilityId == supplierFacilityId
				)
				.map((detail) => detail.id)
		: [];
	return [
		...new Set(
			[
				clientLegId,
				nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.id`, workOrder),
				...ownSubContractedDetailIds,
			].filter(Boolean)
		),
	];
};

export const getTaskListSummaryIfExist = (state) => {
	if (!state.includeWalkthroughTaskList || !state.taskAnswer) return [];
	const isChecklist =
		nullSafeGet('taskAnswer.questionType', state) === SITE_SURVEY_ANSWER_TYPE.CHECKLIST;
	const itemCount = getAnswerItems(state.taskAnswer).length;
	return [
		{
			key: `${isChecklist ? 'Checklist' : 'Task List'} (${itemCount})`,
			lineOneRender: (
				<div className="mb-3">
					<WalkthroughTaskListAnswerDisplay
						taskAnswer={state.taskAnswer}
						includeOnlyPendingTaskItems
						hideDisabledHelper
					/>
				</div>
			),
		},
	];
};

export const getPayloadForPartialUpdateOnNewNote = (workOrder, isClientView) => {
	return isClientView
		? workOrder.subContractedFromWorkOrder
			? {
					id: workOrder.id,
					subContractedFromWorkOrder: {
						...workOrder.subContractedFromWorkOrder,
						[UNREAD_NOTES_NOTIFICATION_KEY]: true,
					},
			  }
			: { id: workOrder.id, [UNREAD_NOTES_NOTIFICATION_KEY]: true }
		: workOrder.subContractedToWorkOrder
		? {
				id: workOrder.id,
				subContractedToWorkOrder: {
					...workOrder.subContractedToWorkOrder,
					[UNREAD_NOTES_NOTIFICATION_KEY]: true,
				},
		  }
		: { id: workOrder.id, [UNREAD_NOTES_NOTIFICATION_KEY]: true };
};

export const getPayloadToMarkNotesRead = (workOrder, isClientView) => {
	return isClientView
		? workOrder.subContractedFromWorkOrder
			? {
					id: workOrder.id,
					subContractedFromWorkOrder: {
						...workOrder.subContractedFromWorkOrder,
						[UNREAD_NOTES_NOTIFICATION_KEY]: false,
					},
			  }
			: { id: workOrder.id, [UNREAD_NOTES_NOTIFICATION_KEY]: false }
		: workOrder.subContractedToWorkOrder
		? {
				id: workOrder.id,
				subContractedToWorkOrder: {
					...workOrder.subContractedToWorkOrder,
					[UNREAD_NOTES_NOTIFICATION_KEY]: false,
				},
		  }
		: { id: workOrder.id, [UNREAD_NOTES_NOTIFICATION_KEY]: false };
};

// Display statuses that count as "in-flight" when looking for duplicate
// candidates — covers everything except completed/cancelled/archived.
export const OPEN_WORK_ORDER_DISPLAY_STATUSES = ['Pending', 'Open', 'InProgress', 'OnHold'];

// When this flag is on, the title-based duplicate check is the canonical
// duplicate-detection step. The legacy asset-step duplicate check is skipped
// because the title check is a strict superset (it includes assetId).
export const isWorkOrderDuplicationEnabled = () =>
	posthog.isFeatureEnabled('workorder-duplication');

// On the asset-pre-known wizards (asset detail → "New WO/SR"), the asset
// step normally runs a duplicate check, then routes to TITLE if the asset
// has a single problem type or PROBLEM_TYPE_TOP_LEVEL otherwise. When the
// duplication flag is on, the asset step skips the check and uses this
// same branching to advance directly.
export const skipAssetDuplicateCheckForKnownAsset = ({
	asset,
	goNextAfterBriefPause,
}: {
	asset: any;
	goNextAfterBriefPause: (validation: any[], destination: string, fromStep: string) => any;
}) => {
	const problemTypeIds = nullSafeGet('assetType.allProblemTypeIds', asset);
	if (problemTypeIds && problemTypeIds.length === 1) {
		return goNextAfterBriefPause([], WO_STEP_NAMES.TITLE, WO_STEP_NAMES.ASSET_CONDITION);
	}
	return goNextAfterBriefPause(
		[],
		WO_STEP_NAMES.PROBLEM_TYPE_TOP_LEVEL,
		WO_STEP_NAMES.ASSET_CONDITION
	);
};

export const buildDuplicateCheckParams = (
	state: any,
	currentUser: any,
	title: string
): Record<string, any> => {
	const assetId = nullSafeGet('asset.id', state);
	const problemTypeHierarchyName = nullSafeGet('problemType.hierarchyName', state);
	const areaHierarchyName = nullSafeGet('area.hierarchyName', state);
	return {
		title,
		locationId: nullSafeGet('location.id', state),
		isPM: false,
		displayStatuses: OPEN_WORK_ORDER_DISPLAY_STATUSES,
		[getWorkOrderArchivalKey(currentUser)]: false,
		...(assetId && { assetId }),
		...(problemTypeHierarchyName && { problemTypeHierarchyName }),
		...(areaHierarchyName && { areaHierarchyName }),
	};
};

// Stable identifiers for the work-order/service-request wizards that emit
// duplicate-check telemetry. Centralised so PostHog funnels don't fragment
// over typoed string literals.
export const WIZARD_NAMES = {
	BUYER_NEW_WORK_ORDER: 'buyer_new_work_order_wizard',
	SUPPLIER_NEW_WORK_ORDER: 'supplier_new_work_order_wizard',
	CONTRACTOR_NEW_WORK_ORDER: 'contractor_new_work_order_wizard',
	BUYER_NEW_SERVICE_REQUEST: 'buyer_new_service_request_wizard',
	ASSET_NEW_SERVICE_REQUEST: 'asset_new_service_request_wizard',
	ASSET_NEW_WORK_ORDER: 'asset_new_work_order_wizard',
	SUPPLIER_ASSET_NEW_WORK_ORDER: 'supplier_asset_new_work_order_wizard',
	CUSTOMER_REQUEST_PORTAL: 'customer_request_portal',
	CLIENT_REQUEST_PORTAL: 'client_request_portal',
} as const;

export type WizardName = typeof WIZARD_NAMES[keyof typeof WIZARD_NAMES];

// Telemetry dimensions logged with work-order pre-creation attributes. These are
// orthogonal axes (kept as separate top-level fields on the payload) so analytics
// can slice creation by where it started, which client emitted it, and who did it
// — without overloading a single string the way the legacy wizard name did.

// Product surface a creation was initiated from. `QR_SCAN` is reserved for the
// native apps (scanning an asset QR code) and is never emitted by web.
export const SOURCE_SURFACES = {
	STANDARD: 'standard',
	ASSET: 'asset',
	QR_SCAN: 'qr_scan',
	CUSTOMER_REQUEST_PORTAL: 'customer_request_portal',
	CLIENT_REQUEST_PORTAL: 'client_request_portal',
} as const;

export type SourceSurface = typeof SOURCE_SURFACES[keyof typeof SOURCE_SURFACES];

// Client that emitted the telemetry. This repo is always `WEB`; the mobile values
// are reserved so native clients can adopt the same schema later.
export const PLATFORMS = {
	WEB: 'web',
	BUYER_MOBILE: 'buyer_mobile',
	SUPPLIER_MOBILE: 'supplier_mobile',
} as const;

export type Platform = typeof PLATFORMS[keyof typeof PLATFORMS];

// Emitted when the request has no authenticated user (external portal submitter).
// Kept in the same uppercase vocabulary as the system role ids that `role` carries.
export const PORTAL_ANONYMOUS_ROLE = 'PORTAL_ANONYMOUS';

// Highest-precedence role wins so each event carries a single, stable role string.
const ROLE_SOURCE_PRECEDENCE = [
	SUPER_ADMIN,
	BUYER_ADMIN,
	BUYER_USER,
	BUYER_STORE_ASSOCIATE,
	SUPPLIER_ADMIN,
	SUPPLIER_MANAGER,
	SUPPLIER_USER,
	SUPPLIER_TECH,
	SUPPLIER_INVENTORY_MANAGER,
	SUPPLIER_ACCOUNTING_MANAGER,
];

// Resolves the acting user's roles down to a single role string for telemetry.
// Falls back to `portal_anonymous` when there is no authenticated user.
export const deriveRoleSource = (currentUser: any): string => {
	const roles = nullSafeGetOrElse('roles', currentUser, []);
	const match = ROLE_SOURCE_PRECEDENCE.find((role) => roles.indexOf(role) !== -1);
	return match || PORTAL_ANONYMOUS_ROLE;
};

// Which candidates count as duplicates is decided by the backend — the UI does not
// apply its own score threshold. It only caps the list at the number of rows the
// duplicate-check step can present, keeping the backend's ranking order.
const MAX_DUPLICATE_WORK_ORDERS_SHOWN = 3;

export const selectDuplicateWorkOrdersForDisplay = (workOrders) =>
	(workOrders || []).slice(0, MAX_DUPLICATE_WORK_ORDERS_SHOWN);

export type DuplicateCheckSource = 'title' | 'asset';

export const captureDuplicateCheckRun = ({
	wizard,
	source,
	rawWorkOrders,
	shownWorkOrders,
	context = {},
}: {
	wizard: WizardName;
	source: DuplicateCheckSource;
	rawWorkOrders: any[];
	shownWorkOrders: any[];
	context?: Record<string, any>;
}) => {
	const matchCount = (rawWorkOrders || []).length;
	const shownCount = (shownWorkOrders || []).length;
	const topScore =
		source === 'title' && shownCount > 0 ? nullSafeGet('score', shownWorkOrders[0]) : undefined;
	posthog.capture('workorder_duplicate_check_run', {
		wizard,
		source,
		matchCount,
		shownCount,
		topScore,
		...context,
	});
};

export const captureDuplicateResolved = ({
	wizard,
	source,
	resp,
	shownCount,
	context = {},
}: {
	wizard: WizardName;
	source: DuplicateCheckSource;
	resp: number;
	shownCount: number;
	context?: Record<string, any>;
}) => {
	posthog.capture('workorder_duplicate_resolved', {
		wizard,
		source,
		action: resp === 1 ? 'cancelled_as_duplicate' : 'proceeded_new',
		shownCount,
		...context,
	});
};

export const logPreCreationAttributes = ({
	state,
	currentUser,
	sendPreCreationAttributesFn,
	source,
	duplicateWorkOrderId,
	createdWorkOrderId,
	createdByEmail,
}: {
	state: any;
	currentUser: any;
	sendPreCreationAttributesFn: (payload: any) => Promise<any>;
	source: SourceSurface;
	duplicateWorkOrderId?: number;
	createdWorkOrderId?: number;
	createdByEmail?: string;
}) => {
	const requestPayload = nullSafeGet('duplicateCheckRequestPayload', state);
	if (!requestPayload) return;

	const buyerCompanyId =
		nullSafeGet('facility.buyerCompanyId', currentUser) ||
		nullSafeGet('location.buyerCompanyId', state);
	if (!buyerCompanyId) return;

	const locationId = nullSafeGet('location.id', state);
	const rawWorkOrderIds = nullSafeGetOrElse('duplicateCheckRawWorkOrderIds', state, []);
	const potentialDuplicateWorkOrderIds = rawWorkOrderIds.map((id) =>
		typeof id === 'number' ? id : parseInt(id, 10)
	);

	const payload: Record<string, any> = {
		buyerCompanyId: parseInt(buyerCompanyId, 10),
		requestPayload: requestPayload,
		potentialDuplicateWorkOrderIds: potentialDuplicateWorkOrderIds,
		source: source,
		platform: PLATFORMS.WEB,
		role: deriveRoleSource(currentUser),
	};

	if (locationId) {
		payload.locationId = parseInt(locationId, 10);
	}
	if (duplicateWorkOrderId !== undefined) {
		payload.duplicateWorkOrderId = duplicateWorkOrderId;
	}
	if (createdWorkOrderId !== undefined) {
		payload.createdWorkOrderId = createdWorkOrderId;
	}
	if (createdByEmail) {
		payload.createdByEmail = createdByEmail;
	}

	sendPreCreationAttributesFn(payload);
};

// Emits structured PostHog logs when a duplicate check looks location-unsafe:
// either it ran with no target location (backend then does a global title search)
// or it returned a candidate from a different location (a cross-club false
// positive). These are the signals behind OPE-5080 — we log them so a PostHog
// Logs alert can surface how often this happens across all wizards and triage it.
const logDuplicateCheckLocationAnomalies = ({
	state,
	currentUser,
	wizard,
	params,
	workOrders,
	shownCount,
}: {
	state: any;
	currentUser: any;
	wizard: WizardName;
	params: Record<string, any>;
	workOrders: any[];
	shownCount: number;
}) => {
	const targetLocationId = nullSafeGet('locationId', params);
	const matchCount = (workOrders || []).length;
	const commonAttributes = {
		wizard,
		matchCount,
		shownCount,
		problemTypeHierarchyName: nullSafeGet('problemType.hierarchyName', state),
		buyerCompanyId: nullSafeGet('facility.buyerCompanyId', currentUser),
		createdByEmail: nullSafeGet('email', currentUser),
	};

	if (!targetLocationId) {
		logWarn(
			LOG_EVENTS.WO_DUPLICATE_CHECK_MISSING_LOCATION,
			'Work order duplicate check ran without a target locationId',
			commonAttributes
		);
		return;
	}

	const crossLocationCandidates = (workOrders || []).filter((workOrder) => {
		const candidateLocationId =
			nullSafeGet('locationId', workOrder) || nullSafeGet('location.id', workOrder);
		return candidateLocationId && !valueCompareWithoutType(candidateLocationId, targetLocationId);
	});

	if (crossLocationCandidates.length > 0) {
		logError(
			LOG_EVENTS.WO_DUPLICATE_CROSS_LOCATION_CANDIDATE,
			'Work order duplicate check returned candidate(s) from a different location',
			{
				...commonAttributes,
				targetLocationId,
				crossLocationCount: crossLocationCandidates.length,
				sampleCandidates: crossLocationCandidates.slice(0, 5).map((workOrder) => ({
					workOrderId: nullSafeGet('id', workOrder),
					locationId: nullSafeGet('locationId', workOrder) || nullSafeGet('location.id', workOrder),
				})),
			}
		);
	}
};

// Shared title-step duplicate-check flow for the work-order/service-request
// wizards. Reads from `state.problemType`, `state.location.id`, `state.asset.id`,
// `state.area.hierarchyName`, and `state.problemType.hierarchyName` — wizard
// state shapes must keep these keys for the helper to work.
export const handleDuplicateCheckOnTitle = ({
	state,
	form,
	currentUser,
	findDuplicateWorkOrders,
	setState,
	setProcessing,
	goNext,
	goNextAfterBriefPause,
	wizard,
	nextStepOnNoDuplicates = WO_STEP_NAMES.PHOTOS,
}: {
	state: any;
	form: any;
	currentUser: any;
	findDuplicateWorkOrders: (params: any) => Promise<any[]>;
	setState: (state: any) => void;
	setProcessing: (value: boolean) => void;
	goNext: (validation: any[], destination: string, fromStep: string) => any;
	goNextAfterBriefPause: (validation: any[], destination: string, fromStep: string) => any;
	wizard: WizardName;
	nextStepOnNoDuplicates?: string;
}) => {
	const currentProblemType = nullSafeGet('problemType', state);
	if (
		!posthog.isFeatureEnabled('workorder-duplication') ||
		nullSafeGet('excludeFromSimilarityDetection', currentProblemType) === true
	) {
		return goNextAfterBriefPause(['title'], nextStepOnNoDuplicates, WO_STEP_NAMES.TITLE);
	}
	form.validateFields(['title'], (err: any, values: any) => {
		if (err) return;
		setProcessing(true);
		const params = buildDuplicateCheckParams(state, currentUser, values.title);
		findDuplicateWorkOrders(params)
			.then((workOrders) => {
				const duplicates = selectDuplicateWorkOrdersForDisplay(workOrders);
				captureDuplicateCheckRun({
					wizard,
					source: 'title',
					rawWorkOrders: workOrders,
					shownWorkOrders: duplicates,
					context: {
						locationId: nullSafeGet('location.id', state),
						problemTypeHierarchyName: nullSafeGet('problemType.hierarchyName', state),
						assetId: nullSafeGet('asset.id', state),
					},
				});
				logDuplicateCheckLocationAnomalies({
					state,
					currentUser,
					wizard,
					params,
					workOrders,
					shownCount: duplicates.length,
				});
				const rawWorkOrderIds = (workOrders || [])
					.map((workOrder) => nullSafeGet('id', workOrder))
					.filter(Boolean);
				if (duplicates.length === 0) {
					setState({
						duplicateCheckRawWorkOrderIds: rawWorkOrderIds,
						duplicateCheckRequestPayload: params,
					});
					goNext([], nextStepOnNoDuplicates, WO_STEP_NAMES.TITLE);
				} else {
					setState({
						duplicateWorkOrders: duplicates,
						duplicateCheckSource: 'title',
						duplicateCheckRawWorkOrderIds: rawWorkOrderIds,
						duplicateCheckRequestPayload: params,
					});
					goNext([], WO_STEP_NAMES.DUPLICATE_CHECK, WO_STEP_NAMES.TITLE);
				}
			})
			.catch(() => setProcessing(false));
	});
};

// True when the showPriorities=false config should hide the priority step
// for the current user. Managers/admins/buyer-users (BUYER_ONLY_NO_STORE_ASSOCIATE)
// bypass the config and always see the priority step; only pure store
// associates get it hidden.
export const shouldHidePriorityFromUser = (companyConfig, currentUser) => {
	const configHides =
		nullSafeGetOrElse('config.newServiceRequest.showPriorities', companyConfig, {}).value === false;
	if (!configHides) return false;
	const isStoreAssociate = !(
		currentUser.roles && currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role))
	);
	return isStoreAssociate;
};

// Builds the location/area scoped filters used when fetching work order attributes.
// countryCode comes from the location object; areaId comes from the selected area
// entity (when present). Empty values are omitted so they never reach the query string.
export const getLocationWorkOrderAttributeFilters = (location, area?) => {
	const countryCode = nullSafeGet('countryCode', location);
	const areaId = nullSafeGet('id', area);
	return {
		...(countryCode ? { countryCode } : {}),
		...(areaId ? { areaId } : {}),
	};
};

// Returns the work order attribute's NTEAmount expressed in the location's currency.
// The attribute carries its own currencyId; when it differs from the location currency we fetch the
// attribute-currency -> location-currency rate and convert. When it matches (or the attribute has no
// currencyId, or there is no NTEAmount) the amount is returned unchanged.
export const getWorkOrderAttributeNTEInLocationCurrency = (
	workOrderAttribute,
	location,
	fetchExchangeRate,
	userType
) => {
	const nteAmount = nullSafeGet('NTEAmount', workOrderAttribute);
	if (!nteAmount) {
		return Promise.resolve(nteAmount);
	}
	const attributeCurrencyId = nullSafeGet('currencyId', workOrderAttribute);
	const locationCurrencyId = getCurrency({ location }).id;
	if (!attributeCurrencyId || attributeCurrencyId === locationCurrencyId) {
		return Promise.resolve(nteAmount);
	}
	return fetchExchangeRate(locationCurrencyId, attributeCurrencyId, userType).then(
		(rate) => nteAmount * nullSafeGetOrElse('conversionRate', rate, 1)
	);
};


export const getServiceCallLeadTechnicianEmail = (workOrder): string =>
	nullSafeGetOrElse(
		'lastServiceCall.leadTechnicianEmail',
		workOrder,
		nullSafeGet('lastServiceCallLeadTechnicianEmail', workOrder)
	);
